// Datadog RUM の初期化と addAction ラッパーを提供するモジュール。
// bootstrapRum() はページロード時に1度だけ呼び出すこと。
// 設定欠落・対象外パス・初期化失敗時はすべて no-op で終了し、アプリケーションを停止させない。
import { datadogRum } from "@datadog/browser-rum";

import { normalizeRumConfig } from "@/observability/rum/config";
import { sanitizeRumContext, sanitizeRumEvent } from "@/observability/rum/sanitizer";
import { isRumTargetPath } from "@/observability/rum/scope";

// window への直接参照を変数に切り出すことで、テスト時のモック差し替えを容易にする
const rumWindow = window;

// 開発環境のみ info ログを出力する。本番環境ではノイズを出さない
const logInfo = (message) => {
  if (import.meta.env.DEV) {
    console.info(`[rum] ${message}`);
  }
};

// 開発・ステージング環境のみ警告ログを出力する
// NOTE: assets:precompile 時の `vite build` には --mode が渡されず、
//   staging でも RAILS_ENV/ビルド ENV は production 固定のため、
//   import.meta.env.MODE は staging では常に "production" になり判定に使えない。
//   そのため Rails 側 (DATADOG_RUM_ENV) から注入される env（例: staging1, staging2）で判定する。
const logWarn = (env, message, error) => {
  if (import.meta.env.DEV || (env ?? "").startsWith("staging")) {
    console.warn(`[rum] ${message}`, error);
  }
};

// RUM を初期化する。以下のいずれかに該当する場合は即 return して no-op とする:
//   1. すでに初期化済み（二重初期化防止）
//   2. 環境変数フラグが無効 or 設定値が不足している
//   3. 現在のパスが対象外（管理画面など）
export const bootstrapRum = () => {
  // 二重初期化を防止する
  if (rumWindow.__KP_RUM_INITIALIZED__) {
    return;
  }

  const config = normalizeRumConfig(rumWindow.__KP_RUM_CONFIG__);
  // 無効フラグまたは必須設定欠落の場合は初期化しない（fail-closed）
  if (!config.enabled) {
    return;
  }

  // 主要導線のみを対象とし、管理画面は計測しない
  if (!isRumTargetPath(window.location.pathname)) {
    return;
  }

  try {
    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site,
      service: config.service,
      env: config.env,
      version: config.version,
      sessionSampleRate: config.sessionSampleRate,
      sessionReplaySampleRate: config.sessionReplaySampleRate,
      trackUserInteractions: config.trackUserInteractions,
      trackResources: config.trackResources,
      trackLongTasks: config.trackLongTasks,
      defaultPrivacyLevel: config.defaultPrivacyLevel,
      // beforeSend で URLクエリ・フォーム入力値などの個人情報をサニタイズする
      beforeSend: sanitizeRumEvent,
    });
    rumWindow.__KP_RUM_INITIALIZED__ = true;
  } catch (error) {
    // 初期化例外時は RUM を無効扱いにしてアプリを継続動作させる
    rumWindow.__KP_RUM_INITIALIZED__ = false;
    logWarn(config.env, "failed to initialize datadog rum", error);
  }
};

// 主要操作イベントを送信するラッパー。
// RUM 未初期化の場合・送信失敗の場合はともに no-op で継続し、アプリを停止させない。
// context の値はサニタイズ処理によってアロー リスト制御とマスキングが適用される。
export const addRumAction = (eventName, context = {}) => {
  if (!rumWindow.__KP_RUM_INITIALIZED__) {
    logInfo(`skip action because rum is not initialized: ${eventName}`);
    return;
  }

  try {
    datadogRum.addAction(eventName, sanitizeRumContext(context));
  } catch {
    logInfo(`skip action due to datadog rum addAction failure: ${eventName}`);
  }
};
